import { httpRequest } from "@/lib/axios";

export type CommandCenterSenderKind = "user" | "agent" | "system";
export type CommandCenterMessageType = "text" | "system_event" | "daily_briefing";

export type CommandCenterSender = {
  kind: CommandCenterSenderKind;
  id: string | null;
  name: string;
  avatar: string | null;
};

export type HitlCardMetadata = {
  kind: "hitl.request.created" | "hitl.request.decided";
  hitl_request_id: string;
  state: "pending" | "approved" | "rejected" | "cancelled" | "expired";
  action_name: string;
  agent_name: string | null;
  decided_by: { id: string; name: string } | null;
  link: string;
};

export type CommandCenterMessage = {
  id: string;
  type: CommandCenterMessageType;
  content: string;
  metadata: Record<string, unknown> | null;
  sender: CommandCenterSender;
  created_at: string;
};

export type CommandCenterMessagePage = {
  data: CommandCenterMessage[];
  next_cursor: string | null;
};

export type CommandCenterParticipant = {
  id: string;
  name: string;
  slug: string;
  avatar?: string | null;
  kind: "user" | "agent";
};

export type CommandCenterParticipants = {
  users: CommandCenterParticipant[];
  agents: CommandCenterParticipant[];
  agents_unavailable: boolean;
};

export const COMMAND_CENTER_PAGE_SIZE = 50;

// Paths are literals inside each call: scripts/api-calls.mjs scans for them.
export const CommandCenterAPI = {
  getMessages(before?: string, limit: number = COMMAND_CENTER_PAGE_SIZE): Promise<CommandCenterMessagePage> {
    const params: Record<string, string | number> = { limit };
    if (before) params.before = before;
    return httpRequest.get<CommandCenterMessagePage>("/command-center/messages", { params }).then((r) => r.data);
  },
  postMessage(content: string): Promise<CommandCenterMessage> {
    return httpRequest.post<CommandCenterMessage>("/command-center/messages", { content }).then((r) => r.data);
  },
  getParticipants(): Promise<CommandCenterParticipants> {
    return httpRequest.get<CommandCenterParticipants>("/command-center/participants").then((r) => r.data);
  },
};

export function hitlMetadataOf(message: CommandCenterMessage): HitlCardMetadata | null {
  const m = message.metadata;
  if (!m || typeof m !== "object") return null;
  const kind = m.kind;
  if (kind !== "hitl.request.created" && kind !== "hitl.request.decided") return null;
  if (typeof m.hitl_request_id !== "string" || typeof m.link !== "string") return null;
  return m as unknown as HitlCardMetadata;
}

/** The Inbox conversation a system card is about, for the kinds that link to one. */
export function conversationLinkIdOf(message: CommandCenterMessage): string | null {
  const metadata = message.metadata as Record<string, unknown> | null | undefined;
  if (!metadata) return null;
  const linked = metadata.kind === "ownership.changed"
    || (metadata.kind === "engine.narration" && metadata.note_kind === "attendance_summary");
  if (!linked) return null;
  const id = metadata.conversation_id;
  return typeof id === "string" && id.trim() !== "" ? id : null;
}

export type CommandCenterMentionKind = "user" | "agent" | "todos";

export type CommandCenterMentionCandidate = { kind: "user" | "agent"; id: string; name: string };

/**
 * What `POST /command-center/messages` resolved, as stored in `metadata.mentions`
 * (slice 3 §2.1). `token` is the exact source text of the match, which is what
 * lets the bubble highlight a range without re-implementing the grammar.
 */
export type CommandCenterMention = {
  token: string;
  kind: CommandCenterMentionKind;
  id: string | null;
  name: string;
  resolved: boolean;
  reason?: string;
  candidates?: CommandCenterMentionCandidate[];
};

export type AgentReplyStatus = "pending" | "replied" | "failed" | "expired";
export type AgentReplyState = { status: AgentReplyStatus; reason: string | null };

const AGENT_REPLY_STATUSES: readonly string[] = ["pending", "replied", "failed", "expired"];

function isMention(value: unknown): value is CommandCenterMention {
  if (typeof value !== "object" || value === null) return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.token === "string" &&
    typeof entry.name === "string" &&
    typeof entry.resolved === "boolean" &&
    (entry.kind === "user" || entry.kind === "agent" || entry.kind === "todos")
  );
}

export function mentionsOf(message: CommandCenterMessage): CommandCenterMention[] {
  const raw = message.metadata?.mentions;
  return Array.isArray(raw) ? raw.filter(isMention) : [];
}

export function mentionsDegraded(message: CommandCenterMessage): boolean {
  return message.metadata?.mentions_degraded === true;
}

/** The agent placeholder lifecycle, or null for anything that is not one. */
export function agentReplyStateOf(message: CommandCenterMessage): AgentReplyState | null {
  const metadata = message.metadata;
  if (!metadata || metadata.kind !== "agent_reply") return null;
  const status = metadata.status;
  if (typeof status !== "string" || !AGENT_REPLY_STATUSES.includes(status)) return null;
  return { status: status as AgentReplyStatus, reason: typeof metadata.reason === "string" ? metadata.reason : null };
}

export type CopilotContinuation = { conversation_id: string; owner_id: string; state: "draft" };
export function copilotContinuationOf(message: CommandCenterMessage): CopilotContinuation | null {
  if (message.sender.kind !== "agent" || message.sender.id !== "COPILOT" || agentReplyStateOf(message)?.status !== "replied") return null;
  const raw = message.metadata?.copilot_continuation;
  if (!raw || typeof raw !== "object") return null;
  const value = raw as Record<string, unknown>;
  return typeof value.conversation_id === "string" && typeof value.owner_id === "string" && value.state === "draft"
    ? { conversation_id: value.conversation_id, owner_id: value.owner_id, state: "draft" } : null;
}
